import { useTheme } from "./shared";

const WARM = "var(--la-warm)";
const WARM_GLOW = "var(--la-warm-glow)";
const COOL = "var(--la-cool)";
const COOL_GLOW = "var(--la-cool-glow)";

const PALETTES = {
    night: {
        surface: "#050506",
        vignette: "rgba(5,5,6,0.85)",
        spot: "radial-gradient(ellipse 70% 45% at 50% 0%, rgba(255,232,182,0.09), transparent 70%)",
        vars: { "--la-warm": "rgba(255,232,182,0.85)", "--la-warm-glow": "rgba(255,214,140,0.45)", "--la-cool": "rgba(212,235,248,0.85)", "--la-cool-glow": "rgba(150,205,240,0.4)" },
    },
    day: {
        surface: "#E9E5DB",
        vignette: "rgba(233,229,219,0.9)",
        spot: "radial-gradient(ellipse 70% 45% at 50% 0%, rgba(255,255,255,0.55), transparent 70%)",
        vars: { "--la-warm": "rgba(148,98,28,0.9)", "--la-warm-glow": "rgba(190,140,50,0.35)", "--la-cool": "rgba(70,98,118,0.9)", "--la-cool-glow": "rgba(90,120,145,0.3)" },
    },
};

const pal = (hue) => (hue === "cool" ? [COOL, COOL_GLOW] : hue === "warm" ? [WARM, WARM_GLOW] : null);

const Rods = ({ hue }) => {
    const heights = [42, 68, 55, 80, 48, 72, 60, 88, 50, 66, 78, 44];
    return (
        <div className="absolute inset-0 flex items-start justify-center gap-[6%] overflow-hidden px-[8%] pt-[12%]">
            {heights.map((h, i) => {
                const p = pal(hue) || (i % 3 === 2 ? [COOL, COOL_GLOW] : [WARM, WARM_GLOW]);
                return (
                    <span
                        key={i}
                        className="block w-px origin-top"
                        style={{
                            height: `${h}%`,
                            background: `linear-gradient(to bottom, transparent, ${p[0]})`,
                            boxShadow: `0 0 18px 1px ${p[1]}`,
                            "--sway-a": `${-10 - (i % 4) * 4}deg`,
                            "--sway-b": `${10 + ((i + 2) % 4) * 4}deg`,
                            animation: `la-sway ${7 + (i % 5)}s ease-in-out ${-i * 0.9}s infinite`,
                        }}
                    />
                );
            })}
        </div>
    );
};

const Rings = ({ hue }) => {
    const p = pal(hue) || [WARM, WARM_GLOW];
    const sizes = [38, 58, 78];
    return (
        <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
            {sizes.map((s, i) => (
                <span
                    key={i}
                    className="absolute rounded-full border"
                    style={{
                        width: `${s}%`,
                        paddingBottom: `${s}%`,
                        borderColor: p[0],
                        opacity: 0.25 + i * 0.2,
                        boxShadow: `0 0 30px 2px ${p[1]}, inset 0 0 30px 2px ${p[1]}`,
                        animation: `${i % 2 ? "la-spin-rev" : "la-spin"} ${14 + i * 9}s linear infinite`,
                    }}
                >
                    <span
                        className="absolute left-1/2 top-0 h-[6px] w-[6px] -translate-x-1/2 -translate-y-1/2 rounded-full"
                        style={{ background: p[0], boxShadow: `0 0 16px 5px ${p[1]}` }}
                    />
                    {i === 2 && (
                        <span
                            className="absolute bottom-0 left-1/2 h-[5px] w-[5px] -translate-x-1/2 translate-y-1/2 rounded-full"
                            style={{ background: p[0], boxShadow: `0 0 14px 4px ${p[1]}` }}
                        />
                    )}
                </span>
            ))}
            <span className="absolute h-2 w-2 rounded-full" style={{ background: p[0], boxShadow: `0 0 24px 6px ${p[1]}`, animation: "la-breathe 5s ease-in-out infinite" }} />
        </div>
    );
};

const Grid = ({ hue }) => {
    const cells = Array.from({ length: 48 });
    return (
        <div className="absolute inset-0 grid grid-cols-8 content-center gap-[7%] overflow-hidden p-[12%]">
            {cells.map((_, i) => {
                const p = pal(hue) || (i % 5 === 0 ? [COOL, COOL_GLOW] : [WARM, WARM_GLOW]);
                return (
                    <span
                        key={i}
                        className="block h-[6px] w-[6px] rounded-full md:h-[7px] md:w-[7px]"
                        style={{
                            background: p[0],
                            boxShadow: `0 0 14px 3px ${p[1]}`,
                            animation: `la-pulse ${3 + (i % 4)}s ease-in-out ${-(i * 0.23)}s infinite`,
                        }}
                    />
                );
            })}
        </div>
    );
};

const Wave = ({ hue }) => {
    const p = pal(hue) || [COOL, COOL_GLOW];
    const lines = [12, 26, 40, 54, 68, 82];
    return (
        <div className="absolute inset-0 overflow-hidden">
            {lines.map((t, i) => (
                <span
                    key={i}
                    className="absolute left-[8%] right-[8%] h-px"
                    style={{
                        top: `${t}%`,
                        background: `linear-gradient(to right, transparent, ${p[0]}, transparent)`,
                        boxShadow: `0 0 16px 1px ${p[1]}`,
                        animation: `la-wave ${5 + i * 0.8}s ease-in-out ${-i * 0.7}s infinite`,
                    }}
                />
            ))}
        </div>
    );
};

const Burst = ({ hue }) => {
    const p = pal(hue) || [WARM, WARM_GLOW];
    return (
        <div className="absolute inset-0 overflow-hidden">
            {Array.from({ length: 8 }).map((_, i) => {
                const a = i * 45 * (Math.PI / 180);
                const x = 50 + 30 * Math.cos(a);
                const y = 50 + 30 * Math.sin(a);
                return (
                    <span
                        key={i}
                        className="absolute block"
                        style={{
                            left: `calc(${x}% - 17px)`,
                            top: `${y}%`,
                            width: 34,
                            height: 2,
                            background: `linear-gradient(to right, transparent, ${p[0]}, transparent)`,
                            boxShadow: `0 0 18px 3px ${p[1]}`,
                            "--sx": `${(i % 2 ? -1 : 1) * (70 + ((i * 53) % 90))}px`,
                            "--sy": `${((i % 3) - 1) * (60 + ((i * 31) % 50))}px`,
                            "--sr": `${(i % 2 ? -1 : 1) * (50 + i * 12)}deg`,
                            "--tr": `${i * 45 + 90}deg`,
                            animation: "la-converge 9s cubic-bezier(0.6, 0, 0.2, 1) infinite",
                        }}
                    />
                );
            })}
            <span
                className="absolute left-1/2 top-1/2 h-[5px] w-[5px] -translate-x-1/2 -translate-y-1/2 rounded-full"
                style={{ background: p[0], boxShadow: `0 0 16px 5px ${p[1]}`, animation: "la-pulse 4.5s ease-in-out infinite" }}
            />
        </div>
    );
};

const Halo = ({ hue }) => {
    const p = pal(hue) || [WARM, WARM_GLOW];
    return (
        <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
            <span
                className="block h-1/2 w-1/2 rounded-full"
                style={{
                    background: `radial-gradient(circle, ${p[1]} 0%, transparent 65%)`,
                    animation: "la-breathe 7s ease-in-out infinite",
                }}
            />
            <span className="absolute h-px w-3/4" style={{ background: `linear-gradient(to right, transparent, ${p[0]}, transparent)`, boxShadow: `0 0 20px 2px ${p[1]}` }} />
        </div>
    );
};

const VARIANTS = { rods: Rods, rings: Rings, grid: Grid, wave: Wave, burst: Burst, halo: Halo };

export const LightArt = ({ variant = "rods", hue = "mixed", className = "", children }) => {
    const Art = VARIANTS[variant] || Rods;
    const { theme } = useTheme();
    const p = PALETTES[theme] || PALETTES.night;
    return (
        <div
            className={`la-surface relative overflow-hidden ${className}`}
            style={{ background: p.surface, ...p.vars }}
            data-testid={`light-art-${variant}`}
        >
            <Art hue={hue} />
            <div className="pointer-events-none absolute inset-0 transition-[background] duration-700" style={{ background: p.spot }} />
            <div
                className="pointer-events-none absolute inset-0 transition-[background] duration-700"
                style={{ background: `radial-gradient(ellipse at center, transparent 30%, ${p.vignette} 100%)` }}
            />
            {children}
        </div>
    );
};
